import React from "react";
import ActionRow from "../ActionRow";
import HintPopover from "../HintPopover.js";
import infoCircleIkona from "../../obr/info-circle.svg";
import { eur, formatujPercentoBezZnamienkaText } from "../../utils/formatovanie";

function jePlatneCislo(hodnota) {
  return Number.isFinite(Number(hodnota));
}

function formatujSumu(hodnota, { znamienko = false } = {}) {
  if (!jePlatneCislo(hodnota)) return "—";

  const cislo = Number(hodnota);
  const prefix = znamienko && cislo > 0 ? "+" : "";

  return `${prefix}${eur(Math.round(cislo))} €`;
}

function formatujKladnuSumuAleboPomlcka(hodnota, { znamienko = false } = {}) {
  const cislo = Number(hodnota);

  if (!Number.isFinite(cislo) || cislo <= 0) return "—";

  return formatujSumu(cislo, { znamienko });
}

function formatujPercento(hodnota, suffix = "") {
  if (!jePlatneCislo(hodnota)) return "—";

  return `${formatujPercentoBezZnamienkaText(Number(hodnota))}${suffix}`;
}

function formatujNasobok(hodnota) {
  if (!jePlatneCislo(hodnota)) return "—";

  return `${Number(hodnota).toFixed(2).replace(".", ",")}×`;
}

function formatujRoky(hodnota) {
  if (!jePlatneCislo(hodnota)) return "—";

  return `${Math.round(Number(hodnota))} r`;
}

function formatujRokyAleboPomlcka(hodnota) {
  const roky = Number(hodnota);

  if (!Number.isFinite(roky) || roky <= 0) return "—";

  return formatujRoky(roky);
}

const vystupInvesticieHint = [
  "Vysvetlenie výstupu investície:",
  "",
  "Priemerné ročné zhodnotenie – ročný výnos investície prepočítaný podľa priebehu vkladov a konečnej hodnoty.",
  "",
  "Celkový investičný horizont – celkový čas, počas ktorého sa investícia zhodnocuje. Ak je zapnuté dodatočné zhodnocovanie, zahŕňa aj roky bez ďalších vkladov.",
  "",
  "Celková hodnota investície – očakávaná hodnota účtu na konci horizontu po započítaní investičných poplatkov, pred prípadnou daňou.",
  "",
  "Celkové vklady – všetky peniaze, ktoré klient vložil do investície. Ak sa z vkladu strhol poplatok, stále je súčasťou vloženej sumy.",
  "",
  "Daň – vypočíta sa iba vtedy, keď je daň zapnutá v karte Poplatky. Počíta sa zo zisku pred zdanením podľa zadanej sadzby dane.",
  "",
  "Čistý výnos – zisk klienta po odpočítaní vložených peňazí, externého jednorazového nákladu a prípadnej dane.",
  "",
  "Poplatky – súčet investičných poplatkov v modeli. Slúži ako samostatná informačná položka, aby sa nemiešala priamo do rozpadu celkovej hodnoty investície.",
  "",
  "Reálna hodnota po inflácii – hodnota investície prepočítaná na dnešnú kúpnu silu podľa zadanej priemernej inflácie.",
  "",
  "Celkové zhodnotenie po inflácii – zhodnotenie po zohľadnení inflácie. Porovnáva reálnu hodnotu investície s reálnou hodnotou vložených peňazí.",
  "",
  "Prínos navýšenia vkladov – o koľko vyšší čistý výnos vznikol vďaka každoročnému navyšovaniu mesačného vkladu.",
  "",
  "Prínos dodatočného zhodnotenia – rozdiel medzi hodnotou po dani s dodatočným zhodnocovaním a hodnotou bez dodatočného zhodnocovania.",
  "",
  "ROI – celkové zhodnotenie vkladu. Ukazuje, koľko čistého výnosu vzniklo voči peniazom, ktoré klient vložil alebo zaplatil ako externý jednorazový náklad.",
  "",
  "Násobok vkladov – koľkokrát sa vložené peniaze zhodnotili. Počíta sa ako celková hodnota investície delená vloženými peniazmi vrátane externého jednorazového nákladu.",
  "",
  "Efekt poplatkov – orientačne ukazuje, o akú časť výnosu klient prišiel vplyvom poplatkov oproti modelu bez poplatkov.",
].join("\n");

function formatujNavysovanieVkladov(data) {
  if (data?.rezimNavysovaniaVkladu === "inflacia") {
    return `infláciu ${formatujPercento(data?.inflacia, " p.a.")}`;
  }

  if (data?.rezimNavysovaniaVkladu === "fix") {
    return `fix ${formatujSumu(data?.fixneNavysenieVkladu)}`;
  }

  return "—";
}

export default function InvesticneVystupyKarta({
  zhodnotenie,
  horizont,
  vysledok,
  vysledkyInvesticii,
  porovnanieInvesticii,
  resetujAplikaciu,
  ulozPonukuPdf,
}) {
  const maPorovnanie = Boolean(porovnanieInvesticii && vysledkyInvesticii?.variant2);

  const varianty = maPorovnanie
    ? [
        { key: "variant1", label: "Variant 1", vysledok: vysledkyInvesticii?.variant1 || vysledok },
        { key: "variant2", label: "Variant 2", vysledok: vysledkyInvesticii?.variant2 },
      ]
    : [
        { key: "variant1", label: "Variant 1", vysledok },
      ];

  const riadky = [
    {
      label: "Priemerné ročné zhodnotenie:",
      hodnota: (data) => formatujPercento(data?.zhodnotenie ?? zhodnotenie, " p.a."),
    },
    {
      label: "Celkový investičný horizont:",
      hodnota: (data) => formatujRoky(data?.celkovaDobaInvesticieRoky ?? horizont),
    },
    {
      label: "Celková hodnota investície:",
      hodnota: (data) => formatujSumu(data?.konecnaHodnota),
    },
    {
      label: "z toho celkové vklady:",
      sub: true,
      hodnota: (data) => formatujSumu(data?.celkoveVklady),
    },
    {
      label: "z toho daň:",
      sub: true,
      hodnota: (data) => formatujSumu(data?.danZVynosu),
    },
    {
      label: "z toho čistý výnos:",
      sub: true,
      hodnota: (data) => formatujSumu(data?.celkovyVynos),
    },
    { divider: true },

    {
      label: "Priemerná ročná inflácia:",
      hodnota: (data) => formatujPercento(data?.inflacia, " p.a."),
    },
    {
      label: "Reálna hodnota po inflácii:",
      hodnota: (data) => formatujSumu(data?.realnaHodnotaPoInflacii),
    },
    {
      label: "Celkové zhodnotenie po inflácii:",
      hodnota: (data) => formatujPercento(data?.realneZhodnoteniePoInflaciiPercento),
    },

    { gap: true },

    {
      label: "Každoročné navýšenie vkladov o:",
      hodnota: (data) => formatujNavysovanieVkladov(data),
    },
    {
      label: "Prínos navýšenia vkladov:",
      hodnota: (data) => formatujKladnuSumuAleboPomlcka(data?.prinosNavyseniaVkladov, { znamienko: true }),
    },

    { gap: true },

    {
      label: "Dodatočné zhodnocovanie:",
      hodnota: (data) => formatujRokyAleboPomlcka(data?.dodatocneZhodnocovanieRoky),
    },
    {
      label: "Prínos dodatočného zhodnotenia:",
      hodnota: (data) => formatujKladnuSumuAleboPomlcka(data?.prinosDodatocnehoZhodnotenia, { znamienko: true }),
    },

    { gap: true },

    {
      label: "Celkové zhodnotenie vkladu (ROI):",
      hodnota: (data) => formatujPercento(data?.celkoveZhodnotenieVkladuPercento ?? data?.roiPercento),
    },
    {
      label: "Násobok vkladov:",
      hodnota: (data) => formatujNasobok(data?.nasobokVkladov),
    },
    {
      label: "Poplatky:",
      hodnota: (data) => formatujSumu(data?.celkovePoplatky),
    },
    {
      label: "Efekt poplatkov:",
      hodnota: (data) => formatujPercento(data?.efektPoplatkovPercento ?? 0),
    },

    { divider: true },
  ].filter(Boolean);

  return (
    <div className="card-section card-section--invest-vystup">
      <div className="card-header invest-vystup-headline">
        <h2>Výstup investície</h2>

        <HintPopover
          obsah={vystupInvesticieHint}
          ariaLabel="Zobraziť vysvetlenie výstupu investície"
          pozicia="top"
          triggerMode="click"
          triggerClassName="porovnavac-ikona invest-vystup-info"
          panelClassName="invest-vystup-info-panel"
        >
          <img src={infoCircleIkona} alt="Info" className="porovnavac-ikona-img" />
        </HintPopover>
      </div>

      <div className="pp-text">
        <div className={`pp-riadky ${maPorovnanie ? "invest-vystup-porovnanie" : "invest-vystup-single"}`}>
          {maPorovnanie && (
            <div className="pp-riadok invest-vystup-head">
              <span className="pp-k" aria-hidden="true"></span>
              {varianty.map((variant) => (
                <span key={variant.key} className="pp-v">{variant.label}</span>
              ))}
            </div>
          )}

          {riadky.map((riadok, index) => {
            if (riadok.gap) {
              return <div key={`gap-${index}`} className="invest-vystup-gap" />;
            }

            if (riadok.divider) {
              return <div key={`divider-${index}`} className="parametre-divider" />;
            }

            return (
              <div key={riadok.label} className={`pp-riadok ${riadok.sub ? "sub" : ""}`}>
                <span className="pp-k">{riadok.label}</span>

                {varianty.map((variant) => (
                  <span key={variant.key} className="pp-v">
                    {maPorovnanie && (
                      <span className="invest-vystup-mobile-label">{variant.label}</span>
                    )}
                    {riadok.hodnota(variant.vysledok || {})}
                  </span>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      <ActionRow onReset={resetujAplikaciu} onUlozPdf={ulozPonukuPdf} />
    </div>
  );
}